import {
  LearningPath,
  LearningNode,
  LearningCategory,
  ValidationCriteria,
  Milestone
} from '../types/learning-path';
import { ConceptCategory, Difficulty } from '../types/index';

const BASIC_VALIDATION: ValidationCriteria = {
  minimumScore: 70,
  requiredAttempts: 2,
  mustDemonstrate: ['correct_syntax', 'basic_logic']
};

const INTERMEDIATE_VALIDATION: ValidationCriteria = {
  minimumScore: 75,
  requiredAttempts: 3,
  mustDemonstrate: ['correct_syntax', 'proper_logic', 'clean_code']
};

// ============================================================================
// PYTHON/JS LEARNING NODES
// ============================================================================

const PYJS_FOUNDATIONS: LearningNode[] = [
  {
    id: 'pyjs_variables_output',
    title: 'Variables, Types & Affichage',
    description: 'Les bases : stocker des données dans des variables et les afficher dans la console',
    category: LearningCategory.PYJS_BASICS,
    prerequisites: [],
    concepts: [ConceptCategory.VARIABLES, ConceptCategory.PYJS_PRINT_OUTPUT],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 20,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 8
  },
  {
    id: 'pyjs_control_flow_functions',
    title: 'Conditions, Boucles & Fonctions',
    description: 'Faire prendre des décisions au code, répéter des actions et organiser le code en fonctions',
    category: LearningCategory.PYJS_CONTROL_FLOW,
    prerequisites: ['pyjs_variables_output'],
    concepts: [ConceptCategory.CONDITIONALS, ConceptCategory.LOOPS, ConceptCategory.METHODS],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 30,
    validationCriteria: BASIC_VALIDATION,
    requiredSuccessfulExercises: 10
  },
  {
    id: 'pyjs_data_structures',
    title: 'Structures de Données',
    description: 'Gérer des collections de données : listes, tableaux et dictionnaires',
    category: LearningCategory.PYJS_DATA_STRUCTURES,
    prerequisites: ['pyjs_control_flow_functions'],
    concepts: [ConceptCategory.PYJS_LISTS_ARRAYS, ConceptCategory.PYJS_DICTIONARIES],
    difficulty: Difficulty.BEGINNER,
    estimatedTime: 35,
    validationCriteria: INTERMEDIATE_VALIDATION,
    requiredSuccessfulExercises: 10
  }
];

// ============================================================================
// PYTHON/JS LEARNING PATH
// ============================================================================

export const PYJS_BEGINNER_PATH: LearningPath = {
  id: 'beginner_python_js_programmer',
  name: 'Programmeur Python/JS Débutant',
  description: 'Parcours complet pour apprendre les bases de la programmation en Python et JavaScript.',
  targetAudience: 'Débutants complets, formation Level Up',
  estimatedDuration: '3-5 semaines',
  nodes: PYJS_FOUNDATIONS,
  milestones: [
    {
      id: 'pyjs_basics_mastery',
      name: 'Les Bases Maîtrisées',
      description: 'Tu sais créer des variables, les afficher et faire des calculs simples',
      requiredNodes: ['pyjs_variables_output'],
      reward: 'Badge Variables & Affichage'
    },
    {
      id: 'pyjs_logic_mastery',
      name: 'Logique & Fonctions',
      description: 'Tu sais utiliser des conditions, des boucles et créer des fonctions',
      requiredNodes: ['pyjs_variables_output', 'pyjs_control_flow_functions'],
      reward: 'Badge Logique de Code'
    },
    {
      id: 'pyjs_data_mastery',
      name: 'Structures de Données',
      description: 'Tu sais manipuler des listes et des dictionnaires pour gérer des données',
      requiredNodes: ['pyjs_variables_output', 'pyjs_control_flow_functions', 'pyjs_data_structures'],
      reward: 'Badge Programmeur Python/JS'
    }
  ]
};

export const PYJS_ALL_PATHS = [PYJS_BEGINNER_PATH];

export const PYJS_ALL_NODES = PYJS_FOUNDATIONS;
